/**
 * Google Ads Conversion Tracking
 *
 * Direct gtag.js implementation for reliable Google Ads conversion tracking.
 * This works independently of GTM and ensures conversions are reported.
 *
 * Setup:
 * 1. Get your Conversion ID from Google Ads (format: AW-XXXXXXXXX)
 * 2. Create conversion actions in Google Ads and get the Conversion Labels
 * 3. Update the CONVERSION_CONFIG below with your values
 */

// Declare gtag on window
declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

/**
 * Google Ads Conversion Configuration
 *
 * To find these values:
 * 1. Go to Google Ads → Goals → Conversions → Summary
 * 2. Click on each conversion action → "Tag setup" → "Use Google Tag Manager"
 * 3. Copy the Conversion ID and Conversion Label
 */
const CONVERSION_CONFIG = {
  // Your Google Ads Conversion ID (same for all conversions in the account)
  // Format: AW-XXXXXXXXX
  conversionId: import.meta.env.VITE_GOOGLE_ADS_CONVERSION_ID || '',

  // Conversion Labels (unique per conversion action)
  // Create these in Google Ads: Goals → Conversions → New conversion action
  labels: {
    // Primary conversions
    signUp: import.meta.env.VITE_GOOGLE_ADS_SIGNUP_LABEL || '',
    purchase: import.meta.env.VITE_GOOGLE_ADS_PURCHASE_LABEL || '',

    // Secondary conversions (optional)
    beginCheckout: import.meta.env.VITE_GOOGLE_ADS_CHECKOUT_LABEL || '',
    onboardingComplete: import.meta.env.VITE_GOOGLE_ADS_ONBOARDING_LABEL || '',
    strategyGenerated: import.meta.env.VITE_GOOGLE_ADS_STRATEGY_LABEL || '',
  },
};

/**
 * Check if Google Ads tracking is configured
 */
function isConfigured(): boolean {
  return Boolean(CONVERSION_CONFIG.conversionId);
}

/**
 * Initialize gtag if not already present
 * This is typically loaded via GTM, but we ensure it exists
 */
function ensureGtag(): void {
  if (typeof window === 'undefined') return;

  // gtag should already be available via GTM
  // If not, we create a minimal implementation that queues to dataLayer
  if (!window.gtag) {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag(...args: unknown[]) {
      window.dataLayer?.push(args);
    };
  }
}

/**
 * Send a conversion event to Google Ads
 */
function sendConversion(
  conversionLabel: string,
  options?: {
    value?: number;
    currency?: string;
    transactionId?: string;
  }
): void {
  if (!isConfigured() || !conversionLabel) {
    // Silently skip if not configured - common in development
    return;
  }

  ensureGtag();

  if (typeof window === 'undefined' || !window.gtag) return;

  const conversionData: Record<string, unknown> = {
    send_to: `${CONVERSION_CONFIG.conversionId}/${conversionLabel}`,
  };

  if (options?.value !== undefined) {
    conversionData.value = options.value;
    conversionData.currency = options.currency || 'USD';
  }

  if (options?.transactionId) {
    conversionData.transaction_id = options.transactionId;
  }

  window.gtag('event', 'conversion', conversionData);

  // Log in development
  if (import.meta.env.DEV) {
    console.log('[Google Ads] Conversion tracked:', {
      label: conversionLabel,
      ...conversionData,
    });
  }
}

// ============================================================================
// Conversion Events
// ============================================================================

/**
 * Track user sign-up conversion
 * Call this after successful user registration
 */
export function trackSignUpConversion(): void {
  sendConversion(CONVERSION_CONFIG.labels.signUp);
}

/**
 * Track purchase conversion
 * Call this after successful payment
 */
export function trackPurchaseConversion(params: {
  value: number;
  currency?: string;
  transactionId: string;
}): void {
  sendConversion(CONVERSION_CONFIG.labels.purchase, {
    value: params.value,
    currency: params.currency || 'USD',
    transactionId: params.transactionId,
  });
}

/**
 * Track begin checkout conversion
 * Call this when user initiates checkout
 */
export function trackBeginCheckoutConversion(params: {
  value: number;
  currency?: string;
}): void {
  if (!CONVERSION_CONFIG.labels.beginCheckout) return;

  sendConversion(CONVERSION_CONFIG.labels.beginCheckout, {
    value: params.value,
    currency: params.currency || 'USD',
  });
}

/**
 * Track onboarding completion conversion
 * Call this when user completes onboarding
 */
export function trackOnboardingCompleteConversion(): void {
  if (!CONVERSION_CONFIG.labels.onboardingComplete) return;

  sendConversion(CONVERSION_CONFIG.labels.onboardingComplete);
}

/**
 * Track strategy generation conversion
 * Call this when user generates their first strategy
 */
export function trackStrategyGeneratedConversion(): void {
  if (!CONVERSION_CONFIG.labels.strategyGenerated) return;

  sendConversion(CONVERSION_CONFIG.labels.strategyGenerated);
}

// ============================================================================
// Convenience export
// ============================================================================

export const googleAdsConversions = {
  trackSignUp: trackSignUpConversion,
  trackPurchase: trackPurchaseConversion,
  trackBeginCheckout: trackBeginCheckoutConversion,
  trackOnboardingComplete: trackOnboardingCompleteConversion,
  trackStrategyGenerated: trackStrategyGeneratedConversion,
  isConfigured,
};
